/**
 * Tela /forgot-password — solicita link de redefinição.
 *
 * Reusa o layout glassmorphism do Login pra manter consistência visual.
 * Backend sempre retorna 200 (não revela se email existe), então o
 * componente mostra mensagem genérica de sucesso após qualquer submit.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail, Send } from "lucide-react";
import toast from "react-hot-toast";

import { authApi } from "@/api/auth";
import { apiErrorMessage } from "@/api/client";
import { Button } from "@/components/Button";
import { BrandSymbol, BrandWordmark } from "@/components/Brand";


export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email);
      setDone(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Falha ao solicitar reset"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-full overflow-hidden">
      <div aria-hidden className="envpulse-bg pointer-events-none absolute inset-0 -z-20" />

      <div className="relative flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="mb-10 flex flex-col items-center gap-5">
            <BrandSymbol className="h-16 w-16" glow />
            <BrandWordmark className="text-4xl" variant="light" />
          </div>

          <div
            className="rounded-2xl border border-white/10 bg-white/[0.025] p-7 backdrop-blur-xl md:p-8"
            style={{ boxShadow: "0 12px 60px -12px rgba(0,0,0,0.6)" }}
          >
            {!done ? (
              <>
                <h2 className="mb-1 text-xl font-semibold text-white">
                  Esqueci minha senha
                </h2>
                <p className="mb-7 text-sm text-slate-400">
                  Informe o email cadastrado e enviaremos um link pra criar uma
                  nova senha.
                </p>

                <form onSubmit={onSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="label-dark">Email</label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                      <input
                        id="email"
                        type="email"
                        required
                        autoComplete="email"
                        autoFocus
                        className="input-dark pl-9"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@empresa.com.br"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    loading={submitting}
                    icon={!submitting ? <Send className="h-4 w-4" /> : undefined}
                    className="w-full"
                  >
                    Enviar link
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <h2 className="mb-2 text-xl font-semibold text-white">
                  Verifique seu email
                </h2>
                <p className="mb-6 text-sm text-slate-400">
                  Se <strong className="text-slate-200">{email}</strong> estiver
                  cadastrado, você receberá um link em alguns segundos.
                  O link expira em 1 hora.
                </p>
                <p className="text-xs text-slate-500">
                  Não chegou? Verifique a pasta de spam ou{" "}
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    className="text-cyan-300 hover:text-cyan-200 hover:underline"
                  >
                    tente outro email
                  </button>
                  .
                </p>
              </div>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
              >
                <ArrowLeft className="h-3 w-3" />
                Voltar pro login
              </Link>
            </div>
          </div>

          <p className="mt-6 text-center text-[10px] text-slate-500">
            uma empresa{" "}
            <span className="font-semibold text-slate-300">LAIT</span>
            {" "}— Latin American Inovação e Tecnologia
          </p>
        </div>
      </div>
    </div>
  );
}
